import {
  Col,
  Row,
  TableColumnsType,
  Tooltip,
  Button as ButtonAnt,
  Typography,
  App
} from 'antd';
import { DeleteOutlined, EditOutlined, EyeOutlined } from '@ant-design/icons';
import IMAGE_DEFAULT from '@/assets/images/image_default.png';
import { Table } from '@/components/tables/table';
import { useModal } from '@/hooks/modal.hook';
import { ProductManagementSearcher } from '@/components/forms/product-management/searcher.form';
import { css } from '@emotion/react';
import { ProductInformationModal } from '@/components/modals/product-management/product-information.modal';
import {
  getListProduct,
  deleteProduct,
  watchListProduct,
  watchProductPagination,
  watchProductLoading,
  watchProductMessage,
  resetMessages
} from '@/redux/slices/product.slice';

import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { useCallback, useEffect, useState } from 'react';
import { PAGINATION_PARAMS } from '@/constants/pagination.constant';
import dayjs from 'dayjs';
import { useQuery } from '@/hooks/query.hook';
import { PermissionMode } from '@/enums/mode.enum';
import { PageURLs } from '@/utils/navigate';
import { setMessages } from '@/redux/slices/app.slice';
import { formatDateToHHmmDDMMYYYY } from '@/utils/common';
import Paragraph from 'antd/es/typography/Paragraph';

type PageChangeParams = {
  page?: number;
  pageSize?: number;
  filter?: FilterProduct;
};

export default function ProductManagementTable({
  setActiveTabProps
}: {
  setActiveTabProps: React.Dispatch<React.SetStateAction<string>>;
}) {
  const { openModal, closeModal } = useModal();
  const dispatch = useAppDispatch();
  const [filter, setFilter] = useState<FilterProduct>({});
  const listProduct = useAppSelector(watchListProduct);
  const pagination = useAppSelector(watchProductPagination);
  const loading = useAppSelector(watchProductLoading);
  const messageInfo = useAppSelector(watchProductMessage);
  const { modal } = App.useApp();
  const { push } = useQuery();

  useEffect(() => {
    dispatch(getListProduct({}));
  }, [dispatch]);

  const handlePageChange = useCallback(
    ({
      page = PAGINATION_PARAMS.offset,
      pageSize = PAGINATION_PARAMS.limit,
      filter
    }: PageChangeParams) => {
      const payload = {
        name: filter?.name || '',
        shortDesc: filter?.shortDesc || '',
        startUpdateDate: filter?.startUpdateDate
          ? dayjs(filter.startUpdateDate).format('YYYY-MM-DD')
          : '',
        endUpdateDate: filter?.endUpdateDate
          ? dayjs(filter.endUpdateDate).format('YYYY-MM-DD')
          : ''
      };

      dispatch(getListProduct({
        query: payload,
        offset: page,
        limit: pageSize
      }));
    },
    [dispatch]
  );

  const handleSearch = (values: FilterProduct) => {
    const isReset = Object.keys(values).length === 0;

    const newFilter = isReset ? {} : { ...filter, ...values };
    setFilter(newFilter);
    handlePageChange({ filter: newFilter });
  };
  const handleDeleteProduct = async (productId: number) => {
    await dispatch(deleteProduct(productId));
    const remainingRecords = listProduct.length - 1;
    if (remainingRecords === 0 && pagination.curPage > 1) {
      handlePageChange({
        page: pagination.curPage - 1,
        pageSize: pagination.limitPage,
        filter
      });
    } else {
      handlePageChange({
        page: pagination.curPage,
        pageSize: pagination.limitPage,
        filter
      });
    }
  };
  const confirmDeleteProduct = (dishId: number) => {
    modal.confirm({
      title: 'Bạn có chắc chắn xoá sản phẩm này?',
      content: (
        <em>
          Dữ liệu không thể khôi phục
        </em>
      ),
      okText: 'Xóa',
      width: 450,
      cancelText: 'Hủy',
      onOk: () => handleDeleteProduct(dishId)
    });
  };
  const handleViewInformation = (product: any) => {
    openModal(
      <ProductInformationModal product={product} onCancel={closeModal} setActiveTabProps={setActiveTabProps} />, { width: 1000 }
    );
  };

  const handleEditInformation = (product: string) => {
    push(
      `${PageURLs.ofProductManagement()}?mode=${PermissionMode.EDIT}&productId=${product}`
    );
  };

  useEffect(() => {
    if (messageInfo) {
      dispatch(
        setMessages([
          {
            type: messageInfo.type,
            message: messageInfo.message,
            description: messageInfo.description
          }
        ])
      );

      dispatch(resetMessages());
    }
  }, [dispatch, messageInfo]);


  const columns: TableColumnsType<any> = [
    {
      title: 'STT',
      render: (_, __, index) => index + 1
    },
    {
      title: 'Tên sản phẩm',
      dataIndex: 'name',
      key: 'name',
      width: 200,
      fixed: 'left',
    },
    {
      title: 'Thực phẩm tương ứng',
      dataIndex: 'foodName',
      key: 'foodName',
      width: 200,
    },
    {
      title: 'ID thực phẩm tương ứng',
      dataIndex: 'idFood',
      key: 'idFood',
      width: 200,
    },
    {
      title: 'Thumbnail',
      dataIndex: 'thumbnail',
      key: 'thumbnail',
      render: (record) => (
        <img
          src={record ? `${record}` : IMAGE_DEFAULT}
          alt='Food Thumbnail'
          style={{ width: '50px', height: '32px', objectFit: 'cover' }}
        />
      )
    },
    {
      title: 'Logo nhãn sản phẩm',
      dataIndex: 'logo',
      key: 'logo',
      render: (record) => (
        <img
          src={record ? `${record}` : IMAGE_DEFAULT}
          alt='Product Logo'
          style={{ width: '50px', height: '32px', objectFit: 'cover' }}
        />
      )
    },
    {
      title: 'Từ khóa nhãn sản phẩm',
      dataIndex: 'tag',
      key: 'tag',
      width: 200,
    },
    {
      title: 'Mô tả ngắn',
      dataIndex: 'shortDesc',
      key: 'shortDesc',
      width: 200,
      render: (text: string) => (
        <Paragraph
          ellipsis={{ rows: 1 }}
          style={{ margin: 0, maxWidth: 200 }}
        >
          {text}
        </Paragraph>
      ),
  
    },
    {
      title: 'Cập nhật mới nhất',
      dataIndex: 'modifiedDate',
      key: 'modifiedDate',
      width: 150,
      render: (_, record) => `${formatDateToHHmmDDMMYYYY(record.modifiedDate)}${record.updatedBy ? ` [${record.updatedBy}]` : ''}`,
    },
    {
      title: 'Ngày tạo',
      dataIndex: 'createdDate',
      key: 'createdDate',
      width: 150,
      render: (_, record) => `${formatDateToHHmmDDMMYYYY(record.createdDate)}${record.createdBy ? ` [${record.createdBy}]` : ''}`,
    },
    {
      title: 'Thao tác',
      key: 'action',
      fixed: 'right',
      width: 104,
      render: (_, record) => (
        <Row gutter={4} wrap={false}>
          <Col>
            <Tooltip title='Xem thông tin'>
              <ButtonAnt
                onClick={() => handleViewInformation(record)}
                type='primary'
                icon={<EyeOutlined />}
                css={viewButtonStyles}
              />
            </Tooltip>
          </Col>
          <Col>
            <Tooltip title='Cập nhật'>
              <ButtonAnt
                onClick={() => handleEditInformation(record.id)}
                type='primary'
                icon={<EditOutlined />}
                css={editButtonStyles}
              />
            </Tooltip>
          </Col>
          <Col>
            <Tooltip title='Xoá'>
              <ButtonAnt
                onClick={() => confirmDeleteProduct(record.id)}
                type='primary'
                danger
                icon={<DeleteOutlined />}
              />
            </Tooltip>
          </Col>
        </Row>
      )
    }
  ];
  return (
    <div css={rootStyles}>
      <ProductManagementSearcher onSearch={handleSearch} />

      <div css={boxStyles}>
        <Typography.Title level={5}>Danh sách sản phẩm AVN</Typography.Title>

        <Table<any>
          columns={columns}
          dataSource={listProduct}
          loading={loading}
          pagination={{
            showQuickJumper: true,
            showSizeChanger: true,
            current: pagination.curPage,
            pageSize: pagination.limitPage,
            total: pagination.totalRows,
            onChange: (page, pageSize) => {
              handlePageChange({ page, pageSize, filter });
            }
          }}
        />
      </div>
    </div>
  );
}

const rootStyles = css`
  background: var(--gray-soft-color);
  display: flex;
  flex-direction: column;
  gap: 1.4rem;
  min-height: calc(100vh - var(--header-height));
`;

const boxStyles = css`
  padding: 1rem;
  background: var(--white-color);
  box-shadow: 0.02rem 0.04rem 0.2rem var(--gray-light-color);
  &:nth-of-type(2) {
    padding: 1.6rem 1rem;
  }

  .ant-table-thead > tr > th {
    background: var(--blue-dark-color) !important;
    color: var(--white-color);
  }

  .ant-table-cell::before {
    display: none;
  }
`;

const viewButtonStyles = css`
  background-color: var(--green-dark-color) !important;
  color: var(--white-color);
`;

const editButtonStyles = css`
  background-color: var(--yellow-light-color) !important;
  color: var(--white-color);
`;